/** Board tab: the LIVE Takt board (WbsBoardView) in read-only mode, rendered
 *  from the snapshot's board rows on its frozen workday axis — same bars, lanes,
 *  leaf headers and colors as the live board, no editing chrome. */
import React from "react";
import type { PullPlanSnapshot } from "../../../../models";
import WbsBoardView from "../../WbsBoardView/WbsBoardView";
import { memberFinish, memberStart } from "../../WbsBoardView/boardLayout";
import DayHeader from "../../PullPlanGrid/DayHeader/DayHeader";
import {
  DAY_PX,
  indexOnAxis,
  LANE_HEIGHT,
  LANE_RAIL_WIDTH,
} from "../../PullPlanGrid/helpers";
import "./BoardTab.css";

const BoardTab: React.FC<{ snapshot: PullPlanSnapshot }> = ({ snapshot }) => {
  const { board_rows, workdays, colors } = snapshot;

  // The snapshot axis spans dep-graph context (external upstream work, far in
  // the past); the BOARD should only span its OWN activities, so the bars fill
  // the view instead of bunching at one end. Trim the workday axis to the board
  // rows' own date range (actuals included, so completed work fits).
  const calendar = React.useMemo(() => {
    const dates: string[] = [];
    for (const m of board_rows) {
      const s = memberStart(m);
      const f = memberFinish(m);
      if (s) dates.push(s.slice(0, 10));
      if (f) dates.push(f.slice(0, 10));
    }
    if (dates.length === 0) return workdays;
    const lo = dates.reduce((a, b) => (a < b ? a : b));
    const hi = dates.reduce((a, b) => (a > b ? a : b));
    const trimmed = workdays.filter((d) => d >= lo && d <= hi);
    return trimmed.length > 0 ? trimmed : workdays;
  }, [board_rows, workdays]);

  const workCodeColors = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(colors).map(([code, c]) => [code, c.fill]),
      ) as Record<string, string>,
    [colors],
  );

  const calendarIndexFor = React.useCallback(
    (iso: string | null): number => indexOnAxis(calendar, iso),
    [calendar],
  );

  const anchorDir = React.useMemo(() => new Map<number, "in" | "out" | "both">(), []);

  // One seed row per WBS leaf so all-completed/empty leaves still render. Key
  // MUST be wbs_path joined by a single space (the leaf key computeLeafRows
  // derives internally) — not the backend's ' / '-joined wbs_groups key.
  const seedLeaves = React.useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ key: string; label: string; path: string[] }> = [];
    for (const m of board_rows) {
      const path = m.wbs_path.length ? m.wbs_path : ["(no WBS node)"];
      const key = path.join(" ");
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, label: path[path.length - 1], path });
    }
    return out;
  }, [board_rows]);

  const gridWidth = LANE_RAIL_WIDTH + calendar.length * DAY_PX;

  return (
    <div className="cpv-board-scroll">
      <div className="cpv-board-body" style={{ width: gridWidth }}>
        <DayHeader calendar={calendar} labelWidth={LANE_RAIL_WIDTH} />
        <WbsBoardView
          members={board_rows}
          seedLeaves={seedLeaves}
          calendar={calendar}
          calendarIndexFor={calendarIndexFor}
          workCodeColors={workCodeColors}
          anchorDir={anchorDir}
          dayPx={DAY_PX}
          labelWidth={LANE_RAIL_WIDTH}
          laneHeight={LANE_HEIGHT}
          readOnly
          showCompleted
        />
      </div>
    </div>
  );
};

export default BoardTab;
